import React from 'react'
import { Card, Flex, Typography } from 'antd'
import { InfoCircleFilled, WarningFilled } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { SeedPedigree } from 'modules/domain/seedTracker/types'
import { DestinationType } from 'modules/domain/seedOrders/types'
import { PedigreeTag } from '../SeedTracker/SeedInventory/PedigreeTag'
import { getAfterSaleColor, isNegativeMtValue } from './inventoryMt'
import {
  inventoryInfoCalcDividerStyle,
  inventoryInfoHintIconStyle,
  inventoryInfoHintRowStyle,
  inventoryInfoHintTextStyle,
  inventoryInfoMainCardStyle,
  inventoryInfoNestedCardStyle,
  inventoryInfoNestedCardWrapStyle,
  inventoryInfoSectionTitleStyle,
  inventoryInfoRowLabelStyle,
  inventoryInfoRowStyle,
  inventoryInfoRowStackedStyle,
  inventoryInfoRowValueStyle,
  inventoryInfoRowValueStackedStyle,
  inventoryInfoWarningIconStyle,
} from './styles'

type CurrentGrowerInventoryCardProps = {
  source?: string
  destination?: string
  destinationType?: DestinationType
  variety?: string
  sourcePedigree?: SeedPedigree
  destinationPedigree?: SeedPedigree
  originCurrentMt?: string
  originRequestedMt?: string
  originAfterSaleMt?: string
  destinationCurrentMt?: string
  destinationRequestedMt?: string
  destinationAfterSaleMt?: string
}

type DisplayRow = {
  label: string
  value?: string
  pedigree?: SeedPedigree
  stackValueBelowLabel?: boolean
  showDividerAfter?: boolean
  valueColor?: string
}

function displayValue(value?: string): string {
  return value?.trim() ? value : '—'
}

function ReadOnlyRows({ rows }: { rows: DisplayRow[] }) {
  return (
    <Flex vertical gap={8}>
      {rows.map((row) => (
        <React.Fragment key={row.label}>
          {row.stackValueBelowLabel ? (
            <Flex vertical style={inventoryInfoRowStackedStyle}>
              <Typography.Text style={inventoryInfoRowLabelStyle}>{row.label}</Typography.Text>
              {row.pedigree ? (
                <Flex justify="space-between" align="center" style={inventoryInfoRowStyle}>
                  <Typography.Text style={inventoryInfoRowValueStackedStyle}>{displayValue(row.value)}</Typography.Text>
                  <PedigreeTag pedigree={row.pedigree} />
                </Flex>
              ) : (
                <Typography.Text style={inventoryInfoRowValueStackedStyle}>{displayValue(row.value)}</Typography.Text>
              )}
            </Flex>
          ) : (
            <Flex justify="space-between" align="center" style={inventoryInfoRowStyle}>
              <Typography.Text style={inventoryInfoRowLabelStyle}>{row.label}</Typography.Text>
              <Typography.Text
                style={{ ...inventoryInfoRowValueStyle, color: row.valueColor ?? inventoryInfoRowValueStyle.color }}
              >
                {displayValue(row.value)}
              </Typography.Text>
            </Flex>
          )}
          {row.showDividerAfter ? <div style={inventoryInfoCalcDividerStyle} aria-hidden /> : null}
        </React.Fragment>
      ))}
    </Flex>
  )
}

const CurrentGrowerInventoryCard: React.FC<CurrentGrowerInventoryCardProps> = ({
  source,
  destination,
  destinationType,
  variety,
  sourcePedigree,
  destinationPedigree,
  originCurrentMt,
  originRequestedMt,
  originAfterSaleMt,
  destinationCurrentMt,
  destinationRequestedMt,
  destinationAfterSaleMt,
}) => {
  const { t } = useTranslation('seedTracker')

  const sourceRows: DisplayRow[] = [
    { label: t('seedOrderInventoryCard.labels.source'), value: source, stackValueBelowLabel: true },
    {
      label: t('seedOrderInventoryCard.labels.variety'),
      value: variety,
      pedigree: sourcePedigree,
      stackValueBelowLabel: true,
    },
    { label: t('seedOrderInventoryCard.labels.currentMt'), value: originCurrentMt },
    { label: t('seedOrderInventoryCard.labels.requestedMt'), value: originRequestedMt, showDividerAfter: true },
    {
      label: t('seedOrderInventoryCard.labels.afterSaleMt'),
      value: originAfterSaleMt,
      valueColor: getAfterSaleColor(originAfterSaleMt),
    },
  ]

  const destinationRows: DisplayRow[] = [
    { label: t('seedOrderInventoryCard.labels.destination'), value: destination, stackValueBelowLabel: true },
    {
      label: t('seedOrderInventoryCard.labels.variety'),
      value: variety,
      pedigree: destinationPedigree,
      stackValueBelowLabel: true,
    },
    { label: t('seedOrderInventoryCard.labels.currentMt'), value: destinationCurrentMt },
    { label: t('seedOrderInventoryCard.labels.requestedMt'), value: destinationRequestedMt, showDividerAfter: true },
    {
      label: t('seedOrderInventoryCard.labels.afterSaleMt'),
      value: destinationAfterSaleMt,
      valueColor: getAfterSaleColor(destinationAfterSaleMt),
    },
  ]

  return (
    <Card bordered style={inventoryInfoMainCardStyle}>
      <Typography.Title level={5} style={inventoryInfoSectionTitleStyle}>
        {t('seedOrderInventoryCard.title')}
      </Typography.Title>
      <div style={inventoryInfoHintRowStyle}>
        <InfoCircleFilled style={inventoryInfoHintIconStyle} />
        <Typography.Text style={inventoryInfoHintTextStyle}>{t('seedOrderInventoryCard.infoText')}</Typography.Text>
      </div>
      <Flex vertical gap={12}>
        <div style={inventoryInfoNestedCardWrapStyle}>
          {isNegativeMtValue(originAfterSaleMt) ? <WarningFilled style={inventoryInfoWarningIconStyle} /> : null}
          <Card size="small" bordered style={inventoryInfoNestedCardStyle}>
            <ReadOnlyRows rows={sourceRows} />
          </Card>
        </div>
        {destinationType === DestinationType.Grower && (
          <div style={inventoryInfoNestedCardWrapStyle}>
            {isNegativeMtValue(destinationAfterSaleMt) ? <WarningFilled style={inventoryInfoWarningIconStyle} /> : null}
            <Card size="small" bordered style={inventoryInfoNestedCardStyle}>
              <ReadOnlyRows rows={destinationRows} />
            </Card>
          </div>
        )}
      </Flex>
    </Card>
  )
}

export default CurrentGrowerInventoryCard
